import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, ShieldCheck, ShieldAlert, Loader2, Activity, Inbox, Cpu, History, LayoutDashboard, RefreshCw } from 'lucide-react';
import ApiMetrics from '@/components/ApiMetrics';
import AdminOverview from '@/components/admin/AdminOverview';
import AdminContactMessages from '@/components/admin/AdminContactMessages';
import AdminAIModels from '@/components/admin/AdminAIModels';
import AdminPickOutcomes from '@/components/admin/AdminPickOutcomes';

const API = process.env.REACT_APP_BACKEND_URL;
const TABS = ['overview', 'metrics', 'messages', 'models', 'outcomes'];

const Blocked = ({ icon: Icon, title, text, action }) => (
  <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-8 text-center max-w-lg mx-auto" data-testid="admin-blocked">
    <Icon className="h-8 w-8 text-amber-400 mx-auto mb-3" />
    <p className="text-white font-semibold">{title}</p>
    <p className="text-sm text-slate-400 mt-1">{text}</p>
    {action}
  </div>
);

const AdminConsole = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = TABS.includes(params.get('tab')) ? params.get('tab') : 'overview';
  const [state, setState] = useState('loading'); // loading | ok | unauth | forbidden | error
  const [overview, setOverview] = useState(null);

  const load = async () => {
    setState('loading');
    try {
      const res = await axios.get(`${API}/api/admin/overview`, { withCredentials: true });
      setOverview(res.data);
      setState('ok');
    } catch (err) {
      const code = err?.response?.status;
      setState(code === 401 ? 'unauth' : code === 403 ? 'forbidden' : 'error');
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="admin-console-page">
      <header className="sticky top-0 z-40 bg-slate-900/95 backdrop-blur-sm border-b border-slate-700 safe-top">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-slate-400 hover:text-white" data-testid="admin-back-button">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to App
          </Button>
          <div className="flex items-center gap-2 text-white font-semibold"><ShieldCheck className="h-5 w-5 text-amber-400" /> Admin Console</div>
          <div className="flex-1" />
          {state === 'ok' && (
            <Button variant="outline" size="sm" onClick={load} className="border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700 hover:text-white" data-testid="admin-refresh-button"><RefreshCw className="h-4 w-4 mr-2" /> Refresh</Button>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {state === 'loading' && <div className="flex items-center gap-3 text-slate-400" data-testid="admin-loading"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>}
        {state === 'unauth' && <Blocked icon={ShieldAlert} title="Please sign in" text="You must be signed in as an administrator to use the console." action={<Button className="mt-4" onClick={() => navigate('/login')} data-testid="admin-go-login">Go to Login</Button>} />}
        {state === 'forbidden' && <Blocked icon={ShieldAlert} title="Administrator access required" text="Your account does not have permission to access the admin console." />}
        {state === 'error' && <Blocked icon={ShieldAlert} title="Couldn't load the console" text="The admin API did not respond." action={<Button className="mt-4" variant="outline" onClick={load}>Try again</Button>} />}

        {state === 'ok' && (
          <Tabs value={tab} onValueChange={(t) => setParams({ tab: t })} className="space-y-6">
            <TabsList className="flex w-full overflow-x-auto no-scrollbar h-auto p-1 gap-0.5 bg-slate-800 border border-slate-700 lg:w-auto lg:inline-flex [&>*]:flex-shrink-0">
              <TabsTrigger value="overview" data-testid="admin-tab-overview"><LayoutDashboard className="h-4 w-4 mr-2" /> Overview</TabsTrigger>
              <TabsTrigger value="metrics" data-testid="admin-tab-metrics"><Activity className="h-4 w-4 mr-2" /> API Monitor</TabsTrigger>
              <TabsTrigger value="messages" data-testid="admin-tab-messages"><Inbox className="h-4 w-4 mr-2" /> Contact Messages{overview?.contact?.unhandled > 0 && <span className="ml-1.5 text-[10px] bg-amber-500 text-slate-900 rounded-full px-1.5">{overview.contact.unhandled}</span>}</TabsTrigger>
              <TabsTrigger value="models" data-testid="admin-tab-models"><Cpu className="h-4 w-4 mr-2" /> AI Model Health</TabsTrigger>
              <TabsTrigger value="outcomes" data-testid="admin-tab-outcomes"><History className="h-4 w-4 mr-2" /> Pick Outcomes</TabsTrigger>
            </TabsList>
            <TabsContent value="overview"><AdminOverview data={overview} onNavigate={(t) => setParams({ tab: t })} /></TabsContent>
            <TabsContent value="metrics"><ApiMetrics /></TabsContent>
            <TabsContent value="messages"><AdminContactMessages onChanged={load} /></TabsContent>
            <TabsContent value="models"><AdminAIModels initial={overview?.ai_models} /></TabsContent>
            <TabsContent value="outcomes"><AdminPickOutcomes /></TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
};

export default AdminConsole;
